import { LoginPage } from '@/components/LoginPage';
import { ActionButton } from '@/components/common/ActionButton';
import { LockIcon } from '@/components/common/icons';
import { CARD_FLOATING, SCREEN } from '@/components/common/surfaces';
import { signinArgs } from '@/config/keycloak';
import { useRoles } from '@/hooks/useRoles';
import type { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';

interface ProtectedAppProps {
  children: ReactNode;
}

/** The corner notice used while the OIDC library is mid-redirect; deliberately not a full screen. */
const STATUS_NOTE_CLASSES = 'fixed bottom-4 right-4 text-xs text-faint';

interface AuthGateProps {
  title: string;
  message?: string;
  icon?: ReactNode;
  actionLabel?: string;
  onAction?: () => void;
}

/** The floating card the app shows instead of a screen it refuses (no role, admin only). */
export const AuthGate: FC<AuthGateProps> = ({ title, message, icon, actionLabel, onAction }) => (
  <div className={`${SCREEN} items-center justify-center px-7 pb-20 pt-10`}>
    <div className={`${CARD_FLOATING} flex w-full max-w-[520px] flex-col gap-3 px-9 pb-[30px] pt-9`}>
      <span className="flex h-[34px] w-[34px] items-center justify-center bg-surface text-muted">
        {icon ?? <LockIcon size={16} />}
      </span>
      <h1 className="m-0 text-[19px] font-bold text-primary [text-wrap:pretty]">{title}</h1>
      {message ? <p className="m-0 text-[13px] leading-[1.6] text-muted">{message}</p> : null}
      {actionLabel && onAction ? (
        <div>
          <ActionButton variant="primary" onClick={onAction}>
            {actionLabel}
          </ActionButton>
        </div>
      ) : null}
    </div>
  </div>
);

/**
 * The sign-in gate around the whole application.
 *
 * A visitor who is not signed in gets the prototype's sign-in screen; its button sends them to the
 * realm's Keycloak login and back to the same URL. A failed callback shows the same screen with
 * the error, never an automatic retry (a misconfigured client would loop). A signed-in user whose
 * token carries none of the application roles gets a refusal card (the backend would answer 403).
 */
export const ProtectedApp: FC<ProtectedAppProps> = ({ children }) => {
  const auth = useAuth();
  const { isUser } = useRoles();
  const { t } = useTranslation();

  switch (auth.activeNavigator) {
    case 'signinSilent':
    case 'signinRedirect':
      return <p className={STATUS_NOTE_CLASSES}>{t('auth.signingIn')}</p>;
    case 'signoutRedirect':
      return <p className={STATUS_NOTE_CLASSES}>{t('auth.signingOut')}</p>;
  }

  if (auth.isLoading) {
    return <p className={STATUS_NOTE_CLASSES}>{t('auth.authenticating')}</p>;
  }
  if (auth.error || !auth.isAuthenticated) {
    return (
      <LoginPage
        onSignIn={() => auth.signinRedirect(signinArgs())}
        error={auth.error ? t('auth.failedTitle') : undefined}
      />
    );
  }
  if (!isUser) {
    return <AuthGate title={t('auth.noRoleTitle')} message={t('auth.noRoleMessage')} />;
  }
  return <>{children}</>;
};
